// @ts-nocheck
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useQuery } from '@apollo/client/react'
import { showDateTime, timeSince } from 'helpers/date/showDate'
import getGroupUrl from 'helpers/getGroupUrl'

import { CoverImage } from 'js/components/CoverImage'
import Document from 'js/components/Document'
import { Container } from 'js/components/Grid'
import { H1 } from 'js/components/Heading'
import HideVisually from 'js/components/HideVisually/HideVisually'
import ToggleTranslation from 'js/components/Item/components/ToggleTranslation'
import ItemActions from 'js/components/Item/ItemActions/ItemActions'
import Author from 'js/components/Item/ItemHeader/components/Author'
import ItemStatusTag from 'js/components/Item/ItemStatusTag'
import ItemSuggestedItems from 'js/components/Item/ItemSuggestedItems'
import ItemTags from 'js/components/ItemTags'
import NestedNav from 'js/components/NestedNav/NestedNav'
import Text from 'js/components/Text/Text'
import TiptapView from 'js/components/Tiptap/TiptapView'
import { Tooltip } from 'js/components/Tooltip/Tooltip'
import { NotFound } from 'js/core/NotFound'
import { globalStateContext } from 'js/lib/GlobalState'
import ThemeProvider from 'js/theme/ThemeProvider'

import getRefetchQueries from './helpers/getRefetchQueries'
import styles from './Item.module.css'
import { WIKI_ITEM } from './Item.queries'

const Item = () => {
    const params = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const { globalState } = useContext(globalStateContext)

    const { data, loading } = useQuery<any>(WIKI_ITEM, {
        variables: {
            guid: params.guid,
            groupGuid: params.groupGuid,
        },
        fetchPolicy: 'cache-and-network',
    })

    const [isTranslated, setIsTranslated] = useState(null)
    useEffect(() => {
        if (!loading && data?.entity) {
            setIsTranslated(!!data.entity.localTitle)
        }
    }, [loading, data])

    if (loading) {
        return null
    }
    if (!data || !data.entity) {
        return <NotFound />
    }

    const { site, entity, viewer } = data

    const refetchQueries = getRefetchQueries(true, !!entity.group)

    const {
        localTitle,
        title,
        url,
        localRichDescription,
        richDescription,
        timeUpdated,
        featured,
        group,
        inGroup,
        suggestedItems,
        hasChildren,
        parent,
        tags,
        tagCategories,
        owner,
        showOwner,
        isTranslationEnabled,
    } = entity

    const hasTranslations = !!localTitle && isTranslationEnabled

    const translatedTitle = isTranslated ? localTitle : title
    const translatedRichDescription = isTranslated
        ? localRichDescription
        : richDescription

    // redirect if there is no groupGuid but entity is inGroup
    if (!params.groupGuid && inGroup) {
        navigate(url, { replace: true })
        return null
    }

    const editMode = globalState.editMode || inGroup

    const onEdit = `${getGroupUrl(params)}/wiki/edit/${entity.guid}`
    const onAfterDelete = parent?.url || `${getGroupUrl(params)}/wiki`

    const hasSuggestedItems = suggestedItems?.length > 0

    const showNav =
        (editMode && viewer.canWriteToContainer) || hasChildren || parent?.guid

    const showAuthor = showOwner && owner

    return (
        <ThemeProvider bodyColor="#FFFFFF">
            <Document title={translatedTitle} containerTitle={group?.name} />

            <div className={styles.wrapper}>
                <Container $noPadding className="WikiContainer">
                    {showNav && (
                        <NestedNav
                            viewer={viewer}
                            editMode={editMode}
                            className="WikiNavigation"
                            entityType="wiki"
                            guid={entity.guid}
                        />
                    )}

                    <article className="WikiArticle">
                        <Container size="normal" className="WikiCoverContainer">
                            <CoverImage featured={featured} />
                        </Container>

                        <Container
                            size="normal"
                            style={{
                                paddingTop: '32px',
                            }}
                        >
                            {showAuthor && (
                                <Author
                                    entity={entity}
                                    style={{ marginBottom: '16px' }}
                                />
                            )}

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <H1 data-testid="item-header-title">
                                    {translatedTitle}
                                    <ItemStatusTag
                                        entity={entity}
                                        style={{ marginBottom: '4px' }}
                                    />
                                </H1>

                                <ItemActions
                                    canDuplicate
                                    showEditButton
                                    entity={entity}
                                    onEdit={onEdit}
                                    moveEntity={parent && entity}
                                    onAfterDelete={onAfterDelete}
                                    refetchQueries={refetchQueries}
                                    style={{ marginLeft: 'auto' }}
                                />
                            </div>

                            <Text size="small" variant="grey">
                                {t('global.last-edited')}:{' '}
                                <Tooltip content={showDateTime(timeUpdated)}>
                                    <time
                                        dateTime={timeUpdated}
                                        aria-hidden={true}
                                    >
                                        {timeSince(timeUpdated)}
                                    </time>
                                </Tooltip>
                                <HideVisually>
                                    {showDateTime(timeUpdated)}
                                </HideVisually>
                            </Text>

                            <TiptapView
                                content={translatedRichDescription}
                                style={{ marginTop: '24px' }}
                            />

                            {hasTranslations && (
                                <ToggleTranslation
                                    isTranslated={isTranslated}
                                    setIsTranslated={setIsTranslated}
                                    style={{ marginTop: '12px' }}
                                />
                            )}

                            <ItemTags
                                showCustomTags={site.showCustomTagsInDetail}
                                showTags={site.showTagsInDetail}
                                customTags={tags}
                                tagCategories={tagCategories}
                                style={{ marginTop: '24px' }}
                            />
                        </Container>
                    </article>

                    {hasSuggestedItems && (
                        <ItemSuggestedItems
                            entity={entity}
                            style={{ marginTop: '32px' }}
                        />
                    )}
                </Container>
            </div>
        </ThemeProvider>
    )
}

export default Item
